import "../IdentityCheck/IVV/style.scss";

import { Button, ButtonVariant } from "@amzn/stencil-react-components/button";
import { Checkbox } from "@amzn/stencil-react-components/checkbox";
import { Row, Spacer } from "@amzn/stencil-react-components/layout";
import {
  MessageBanner,
  MessageBannerType,
} from "@amzn/stencil-react-components/message-banner";
import { Spinner, SpinnerSize } from "@amzn/stencil-react-components/spinner";
import { Label, Text } from "@amzn/stencil-react-components/text";
import { trackStructEvent } from "@snowplow/browser-tracker";
import React, {
  ReactNode,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { FormattedMessage } from "react-intl";

import { updateOnboardingStep } from "@/apis/UserOnboardingStepHelper";
import ErrorBanner from "@/components/Commons/ErrorBanner";
import { STEP_ID } from "@/components/constants";
import { ChatWithITSupportFlyoutLink } from "@/components/GetSupport";
import { getManualConsent } from "@/helpers/consent/manualConsentData";
import { useSubmitUserConsentV2 } from "@/hooks/useSubmitUserConsentV2";
import { AppContext } from "@/stores/appStore";
import { ActionType } from "@/stores/constants";
import {
  Day1EventAction,
  Day1EventCategory,
  Day1EventLabel,
} from "@/types/snowplow-events";

export const DATA_TEST_ID_MANUAL_LEGAL_DISCLOSURE_NO_CONSENT =
  "ManualLegalDisclosureNoConsent";

/**
 * Implements Manual Verification Legal Disclosure dialog.
 * Mounts children after disclosure has been agreed to.
 */
export const ManualLegalDisclosure = ({
  children,
}: {
  children: ReactNode | ReactNode[];
}) => {
  const { dispatch, state } = useContext(AppContext);
  const [consentCheckboxChecked, setConsentCheckboxChecked] = useState(false);
  const [showErrorConsentRequired, setShowErrorConsentRequired] =
    useState(false);
  const {
    isError: isUserConsentsError,
    isLoading: isUserConsentsSubmitting,
    isSuccess: isUserConsentsSubmitted,
    mutate: submitUserConsentV2,
  } = useSubmitUserConsentV2();

  const consentCheckBoxId = "ManualConsentCheckbox";

  const consent = useMemo(
    () => getManualConsent(state.hiringCountry ?? ""),
    [state.hiringCountry]
  );

  /**
   * Set the refPage to provide context to IT Support should the new hire initiate
   * a chat with support
   */
  useEffect(() => {
    dispatch({
      type: ActionType.SET_CHAT_SUPPORT_REF_PAGE,
      chatSupportRefPage: "ezo-identity-check-manual-consent",
    });
  }, []);

  useEffect(() => {
    trackStructEvent({
      category: Day1EventCategory.RIVManualConsentOptin,
      action: Day1EventAction.SectionToggled,
    });

    void updateOnboardingStep("D1_LOGIN");

    dispatch({
      stepId: STEP_ID.IDENTITY_CHECK,
      type: ActionType.SET_HIDE_NEXT,
      hideNext: true,
    });

    return () => {
      dispatch({
        stepId: STEP_ID.IDENTITY_CHECK,
        type: ActionType.SET_HIDE_NEXT,
        hideNext: false,
      });
    };
  }, []);

  const handleSubmitClick = (): void => {
    trackStructEvent({
      category: Day1EventCategory.RIVManualConsentOptin,
      action: Day1EventAction.ClickAgree,
      label: consentCheckboxChecked
        ? Day1EventLabel.OptedIn
        : Day1EventLabel.NotOptedIn,
    });
    setShowErrorConsentRequired(!consentCheckboxChecked);
    if (consentCheckboxChecked) {
      submitUserConsentV2({
        consentCategory: "MANUAL_DATA_COLLECTION",
        consentGranted: true,
      });
    }
  };

  const handleCancelButtonClick = (): void => {
    trackStructEvent({
      category: Day1EventCategory.RIVManualConsentOptin,
      action: Day1EventAction.ClickCancel,
    });

    dispatch({
      type: ActionType.SET_WIZARD_PATH,
      wizardPath: undefined,
    });
  };

  const hasTrackedBottom = useRef(false);
  const hasTrackedTop = useRef(false);

  const handleDisclosureScroll = (evt: React.UIEvent<HTMLElement>) => {
    const { scrollHeight, scrollTop, clientHeight } = evt.currentTarget;

    if (
      !hasTrackedBottom.current &&
      Math.abs(scrollHeight - scrollTop - clientHeight) < 1
    ) {
      hasTrackedBottom.current = true;
      trackStructEvent({
        category: Day1EventCategory.RIVManualConsentOptin,
        action: Day1EventAction.ConsentScrollBottom,
      });
    }

    if (!hasTrackedTop.current && scrollTop === 0) {
      hasTrackedTop.current = true;
      trackStructEvent({
        category: Day1EventCategory.RIVManualConsentOptin,
        action: Day1EventAction.ConsentScrollTop,
      });
    }
  };

  if (isUserConsentsSubmitting) {
    return (
      <Spinner
        dataTestId="manualConsentsSubmittingSpinner"
        showText
        size={SpinnerSize.Medium}
      />
    );
  }

  if (isUserConsentsError) {
    return (
      <>
        <ErrorBanner>
          <FormattedMessage
            id="error-somethingWentWrong"
            values={{
              chatWithITSupportLink: (
                <ChatWithITSupportFlyoutLink localeMessageId="text-chatWithITSupport" />
              ),
            }}
          />
        </ErrorBanner>
        <Row height="S400" />
        <Row justifyContent="right">
          <Button
            variant={ButtonVariant.Secondary}
            onClick={handleCancelButtonClick}
            dataTestId="ManualCancelButton"
          >
            {consent.BackButton}
          </Button>
        </Row>
      </>
    );
  }

  if (!isUserConsentsSubmitted) {
    return (
      <div data-test-id={DATA_TEST_ID_MANUAL_LEGAL_DISCLOSURE_NO_CONSENT}>
        <Spacer height="S300" />
        <Row height="S500" />
        <Row justifyContent="center" alignItems="center">
          <div
            className="legal-disclosure-scrollable line-height-24px"
            onScroll={handleDisclosureScroll}
            data-test-id="manual-legal-disclosure-scrollable"
          >
            <Text fontWeight={"bold"} dataTestId="ManualLegalDisclosureTitle">
              {consent.Header}
            </Text>
            <Text dataTestId="ManualLegalDisclosureBody">
              <FormattedMessage
                id="manual-consent-body"
                defaultMessage={consent.LegalBody}
                values={{
                  p: (chunks: unknown) => (
                    <div className="paragraph">{chunks}</div>
                  ),
                  b: (chunks: unknown) => <strong>{chunks}</strong>,
                  u: (chunks: unknown) => <u>{chunks}</u>,
                  ul: (chunks: unknown) => <ul>{chunks}</ul>,
                  li: (chunks: unknown) => <li>{chunks}</li>,
                }}
              />
            </Text>
          </div>
        </Row>
        <Row height="S400" />
        <Row gridGap="S400">
          <Checkbox
            id={consentCheckBoxId}
            checked={consentCheckboxChecked}
            onChange={(evt) => {
              trackStructEvent({
                category: Day1EventCategory.RIVManualConsentOptin,
                action: evt.target.checked
                  ? Day1EventAction.CheckOptIn
                  : Day1EventAction.UncheckOptIn,
                label: evt.target.checked
                  ? Day1EventLabel.ConsentConfirmedTrue
                  : Day1EventLabel.ConsentConfirmedFalse,
              });
              setConsentCheckboxChecked(evt.target.checked);
            }}
            dataTestId={consentCheckBoxId}
          />
          <Label color="color.text.primary" htmlFor={consentCheckBoxId}>
            <Text
              fontSize="T200"
              className="mouse-pointer"
              dataTestId={`${consentCheckBoxId}Body`}
            >
              {consent.ConsentCheck}
            </Text>
          </Label>
        </Row>
        <Row height="S500" />
        {showErrorConsentRequired && (
          <>
            <Row>
              <MessageBanner
                type={MessageBannerType.Error}
                dataTestId="ManualAgreeCheckboxRequired"
              >
                <FormattedMessage
                  id="manual-consent-checkbox-required"
                  defaultMessage='Please check the box above and click "{consentButton}" to proceed.'
                  values={{
                    consentButton: consent.ConsentButton,
                  }}
                />
              </MessageBanner>
            </Row>
            <Row height="S400" />
          </>
        )}
        <Row justifyContent="right">
          <Button
            variant={ButtonVariant.Secondary}
            onClick={handleCancelButtonClick}
            dataTestId="ManualCancelButton"
          >
            {consent.BackButton}
          </Button>
          <Spacer width="S200" />
          <Button
            variant={ButtonVariant.Primary}
            onClick={handleSubmitClick}
            dataTestId="ManualConsentAgreeButton"
          >
            {consent.ConsentButton}
          </Button>
        </Row>
      </div>
    );
  } else {
    return <>{children}</>;
  }
};
